/**
 * BudgetFitter Contact Wizard — step navigation + AJAX submit.
 * Scoped to #page-contact-wizard; no globals except the bfwiz* functions
 * that are called inline from the PHP template's onclick attributes.
 */
(function () {
  'use strict';

  let currentPersona = '';

  // ── Persona configuration ────────────────────────────────────────────────
  const PERSONAS = {
    visitor: {
      step2Title: 'What can we help with?',
      step2Subtitle:
        'We read every message and typically reply within 2 business days.',
      selectLabel: 'What best describes your request?',
      selectOptions: [
        'Bug or broken deal / link',
        'Discount code not working',
        'Suggest a missing brand',
        'App or browser extension issue',
        'Accessibility concern',
        'General feedback or improvement idea',
        'Other',
      ],
      textareaLabel: 'Tell us more',
      textareaPlaceholder:
        'Describe the issue or suggestion in as much detail as possible. A link to the page helps a lot!',
      companyLabel: 'City / Country',
      websiteLabel: 'Website',
      step3Subtitle:
        'We\'ll review your message and get back to you within 2 business days.',
      successMessage:
        'Thanks for your message! Our team reads every submission and will respond within 2 business days.',
    },
    brand: {
      step2Title: 'How would you like to work with us?',
      step2Subtitle:
        'Our partnerships desk evaluates all merchant submissions against our BFS trust criteria within 5–10 business days.',
      selectLabel: 'What type of partnership are you interested in?',
      selectOptions: [
        'List our current deals and discounts',
        'Featured deal campaign',
        'Sponsored listing or display advertising',
        'Exclusive promo code partnership',
        'Cashback or loyalty integration',
        'Media kit enquiry',
        'Other partnership',
      ],
      textareaLabel: 'Tell us about your brand',
      textareaPlaceholder:
        'e.g., We\'re a UK SaaS platform with 50k+ monthly customers. We\'d love to feature our annual subscriptions on BudgetFitter and discuss an exclusive code...',
      companyLabel: 'Brand / Company Name',
      websiteLabel: 'Brand Website',
      step3Subtitle:
        'Our partnerships team will evaluate your brand against our editorial standards and get back to you within 5–10 business days.',
      successMessage:
        'Thanks for getting in touch! Our partnerships desk will review your brand against our BFS criteria and reach out within 5–10 business days.',
    },
    network: {
      step2Title: 'What would you like to discuss?',
      step2Subtitle:
        'We work with Awin, Impact, and other major networks — commercial partnership proposals are reviewed within 5–10 business days.',
      selectLabel: 'What is your primary goal?',
      selectOptions: [
        'Increase commission rates for our brands',
        'Offer an exclusive promotional code',
        'Secure extra visibility or featured placement',
        'Grow new brand partnerships on BudgetFitter',
        'Publisher development collaboration',
        'Discuss existing brand performance',
        'Other',
      ],
      textareaLabel: 'Give us more context',
      textareaPlaceholder:
        'e.g., We manage 20+ UK brands on Awin and want to discuss exclusive code opportunities and featured placements heading into Q4...',
      companyLabel: 'Network / Company Name',
      websiteLabel: 'Network Website',
      step3Subtitle:
        'We\'ll route your message to our commercial partnerships desk and reply within 5–10 business days.',
      successMessage:
        'Thanks for reaching out! We\'ve received your partnership proposal and our commercial desk will get back to you within 5–10 business days.',
    },
    writer: {
      step2Title: 'Tell us about your writing.',
      step2Subtitle:
        'Our editorial team reviews all pitches and replies within 5 business days.',
      selectLabel: 'What type of contribution are you interested in?',
      selectOptions: [
        'Organic contributor (unpaid editorial)',
        'Regular column or series',
        'Product or brand review pitch',
        'Sponsored guest post',
        'Brand-sponsored review or feature',
        'Other',
      ],
      textareaLabel: 'Your pitch',
      textareaPlaceholder:
        'Tell us about your writing background, what topics you\'d like to cover, and include links to previous work or samples if you have them...',
      companyLabel: 'Publication / Website',
      websiteLabel: 'Portfolio / Writing Samples',
      step3Subtitle:
        'Our editorial team will review your pitch and get back to you within 5 business days.',
      successMessage:
        'Thanks for your pitch! Our editorial team reviews every submission and will get back to you within 5 business days.',
    },
  };

  // ── Helpers ──────────────────────────────────────────────────────────────

  function setLabel(id, active) {
    const el = document.getElementById(id);
    if (!el) return;
    if (active) {
      el.classList.remove('tw-text-tertiary');
      el.classList.add('tw-text-accent');
    } else {
      el.classList.remove('tw-text-accent');
      el.classList.add('tw-text-tertiary');
    }
  }

  function showError(msg) {
    window.BFWT_Toast?.error(msg);
  }

  function clearError() {
    // no-op — Toast auto-dismisses
  }

  function setSubmitting(busy) {
    const btn = document.getElementById('bfwiz-submit-btn');
    const label = document.getElementById('bfwiz-submit-label');
    const icon = document.getElementById('bfwiz-submit-icon');
    if (!btn) return;
    btn.disabled = busy;
    if (label) label.textContent = busy ? 'Sending…' : 'Send Message';
    if (icon) icon.textContent = busy ? 'progress_activity' : 'send';
  }

  // ── Step navigation ──────────────────────────────────────────────────────

  window.bfwizGoToStep = function (step) {
    document.querySelectorAll('.bfwiz-step').forEach((el) => {
      el.classList.add('tw-hidden', 'tw-opacity-0');
      el.classList.remove('tw-opacity-100');
    });

    // Hero intro collapses once user moves past step 1
    const heroIntro = document.getElementById('bfwiz-hero-intro');
    if (heroIntro) {
      if (step === 1) {
        heroIntro.classList.remove('tw-hidden');
      } else {
        heroIntro.classList.add('tw-hidden');
      }
    }

    const bar = document.getElementById('bfwiz-progress-bar');
    if (bar) bar.style.width = step === 1 ? '33%' : step === 2 ? '66%' : '100%';

    setLabel('bfwiz-label-1', step === 1);
    setLabel('bfwiz-label-2', step === 2);
    setLabel('bfwiz-label-3', step === 3);

    const target = document.getElementById('bfwiz-step-' + step);
    if (target) {
      target.classList.remove('tw-hidden');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          target.classList.remove('tw-opacity-0');
          target.classList.add('tw-opacity-100');
        });
      });
    }

    const form = document.getElementById('bfwiz-form');
    if (form) form.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ── Persona selection (Step 1 → Step 2) ─────────────────────────────────

  window.bfwizSelectPersona = function (persona) {
    const cfg = PERSONAS[persona];
    if (!cfg) return;
    currentPersona = persona;

    // Store persona value
    const personaInput = document.getElementById('bfwiz-field-persona');
    if (personaInput) personaInput.value = persona;

    // Update step 2 header copy
    const t = document.getElementById('bfwiz-step2-title');
    const s = document.getElementById('bfwiz-step2-subtitle');
    if (t) t.textContent = cfg.step2Title;
    if (s) s.textContent = cfg.step2Subtitle;

    // Populate select
    const selectLabel = document.getElementById('bfwiz-select-label');
    const select = document.getElementById('bfwiz-field-select');
    if (selectLabel) selectLabel.textContent = cfg.selectLabel;
    if (select) {
      select.innerHTML = '';
      cfg.selectOptions.forEach((opt) => {
        const o = document.createElement('option');
        o.value = opt;
        o.textContent = opt;
        select.appendChild(o);
      });
    }

    // Update textarea
    const taLabel = document.getElementById('bfwiz-textarea-label');
    const ta = document.getElementById('bfwiz-field-message');
    if (taLabel) taLabel.textContent = cfg.textareaLabel;
    if (ta) ta.placeholder = cfg.textareaPlaceholder;

    // Update step 3 dynamic labels (preserve Optional span)
    function relabel(labelId, newText) {
      const lbl = document.getElementById(labelId);
      if (!lbl) return;
      const optionalSpan = lbl.querySelector('.bfwiz-optional-tag');
      lbl.textContent = newText + ' ';
      if (optionalSpan) lbl.appendChild(optionalSpan);
    }
    relabel('bfwiz-company-label', cfg.companyLabel);
    relabel('bfwiz-website-label', cfg.websiteLabel);

    const step3Sub = document.getElementById('bfwiz-step3-subtitle');
    if (step3Sub) step3Sub.textContent = cfg.step3Subtitle;

    window.bfwizGoToStep(2);
  };

  // ── Form submission ──────────────────────────────────────────────────────

  window.bfwizSubmitForm = async function () {
    clearError();

    const emailField = document.getElementById('bfwiz-field-email');
    const email = emailField ? emailField.value.trim() : '';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showError('Please enter a valid email address so we can reach you.');
      if (emailField) emailField.focus();
      return;
    }

    const form = document.getElementById('bfwiz-form');

    setSubmitting(true);

    // Wait for Turnstile token
    try {
      await window.bfwtTurnstile.ensureToken(form);
    } catch (err) {
      showError('Security check failed. Please try again.');
      window.bfwtTurnstile.reset(form);
      setSubmitting(false);
      return;
    }

    const formData = new FormData(form);

    // Mirror the selected sub_type value into the hidden field
    const select = document.getElementById('bfwiz-field-select');
    if (select) {
      formData.set('sub_type', select.value);
      formData.set('sub_type_label', select.value);
    }

    const ajaxUrl =
      (window.bfwizVars && window.bfwizVars.ajaxUrl) ||
      '/wp-admin/admin-ajax.php';

    fetch(ajaxUrl, {
      method: 'POST',
      credentials: 'same-origin',
      body: formData,
    })
      .then((res) =>
        res.json().catch(() => ({
          success: false,
          data: { message: 'Unexpected server response.' },
        }))
      )
      .then((payload) => {
        if (payload && payload.success) {
          // Update persona-specific success message
          const cfg = PERSONAS[currentPersona];
          const msg = document.getElementById('bfwiz-success-message');
          if (cfg && msg) msg.textContent = cfg.successMessage;

          // Hide step 3 + progress bar, show success
          document.getElementById('bfwiz-step-3')?.classList.add('tw-hidden');
          document
            .getElementById('bfwiz-progress-wrap')
            ?.classList.add('tw-hidden');

          const success = document.getElementById('bfwiz-step-success');
          if (success) {
            success.classList.remove('tw-hidden');
            requestAnimationFrame(() =>
              requestAnimationFrame(() => {
                success.classList.remove('tw-opacity-0');
                success.classList.add('tw-opacity-100');
              })
            );
          }
        } else {
          const message =
            payload?.data?.message || 'Something went wrong. Please try again.';
          showError(message);
          setSubmitting(false);
          window.bfwtTurnstile.reset(form);
        }
      })
      .catch(() => {
        showError('Network error. Please check your connection and try again.');
        setSubmitting(false);
        window.bfwtTurnstile.reset(form);
      });
  };

  // ── URL Query & Embed WebView pre-selection ──────────────────────────────
  // If ?persona=brand&option=... is in the URL, or window.bfwizBootArgs is injected,
  // skip step 1 and pre-select the relevant options automatically.

  document.addEventListener('DOMContentLoaded', function () {
    const urlParams = new URLSearchParams(window.location.search);
    const queryPersona = urlParams.get('persona') || urlParams.get('type');
    const querySubType = urlParams.get('option') || urlParams.get('sub_type') || urlParams.get('subtype');

    const args = window.bfwizBootArgs || (queryPersona ? { persona: queryPersona, subType: querySubType } : null);

    if (args && args.persona && PERSONAS[args.persona]) {
      window.bfwizSelectPersona(args.persona);
      if (args.subType) {
        const select = document.getElementById('bfwiz-field-select');
        if (select) {
          const target = args.subType.toLowerCase();
          const opt = Array.from(select.options).find(
            (o) => o.value.toLowerCase().includes(target) || o.text.toLowerCase().includes(target)
          );
          if (opt) {
            select.value = opt.value;
            const subTypeHidden = document.getElementById('bfwiz-field-sub-type');
            if (subTypeHidden) subTypeHidden.value = opt.value;
          }
        }
      }
    }
  });
})();
